import { UseCaseTemplate } from "@/components/institutional/UseCaseTemplate";

const EarlyWarning = () => (
  <UseCaseTemplate
    seoTitle="Early-Warning Signals — Use Case"
    seoDescription="Surface behavioral shifts weeks before grades and attendance reflect them. Explainable, attributable early-warning indicators designed for human review by advisors and student success teams."
    canonicalUrl="/use-cases/early-warning/"
    eyebrow="Use case · Early-warning signals"
    h1={<>See the behavioral shift before it becomes an academic problem.</>}
    intro={<>By the time grades drop, the behavioral patterns associated with disengagement have often been
      present for weeks. CSW surfaces those patterns earlier, with explainable evidence, so advisors can
      decide whether to reach out — and with what context.</>}
    challenge={<>
      Most early-alert systems are downstream of academic events: a missed assignment, a low quiz score,
      an absence pattern. Those signals are valuable, but they arrive after the engagement shift has
      already happened. Student success teams want to be in the conversation sooner — without making
      assumptions about why a student's behavior changed.
    </>}
    approach={[
      { title: "Establish a behavioral baseline per student", description: "CSW analyzes engagement signals within the platform to understand each student's own cadence, focus consistency, and study rhythm." },
      { title: "Detect shifts against that baseline", description: "When behavioral patterns drift in ways commonly associated with disengagement, the platform surfaces a risk indicator — never a verdict." },
      { title: "Present explainable evidence to advisors", description: "Each indicator includes the specific behavioral evidence it is based on, so advisors can decide what (if anything) to do." },
    ]}
    outcome={<>
      An advisor opening their CSW dashboard sees a small list of students whose behavioral patterns
      have shifted recently. Each signal card includes the evidence: "session frequency down vs. own
      baseline over the past 14 days," "focus consistency declining," "participation rhythm has changed."
      The advisor decides whether the shift warrants a check-in. The platform doesn't make that call.
    </>}
    trustNote="Early-warning signals are not predictions of academic outcomes, diagnoses of any kind, or recommendations to take action. They are explainable behavioral patterns presented for human review."
    relatedAudiences={[
      { href: "/for/universities/", label: "CSW for Universities" },
      { href: "/for/colleges/", label: "CSW for Colleges" },
      { href: "/for/high-schools/", label: "CSW for High Schools" },
    ]}
    relatedPlatform={{ href: "/platform/risk-indicators/", label: "Platform: Risk indicators" }}
    relatedUseCases={[
      { href: "/use-cases/retention/", title: "Retention support" },
      { href: "/use-cases/advising-workflows/", title: "Advising workflows" },
      { href: "/use-cases/at-risk-cohorts/", title: "At-risk cohorts" },
    ]}
  />
);
export default EarlyWarning;
